import { Box, ButtonBase, Stack } from '@mui/material';
import { CleaningMissionSortOrder, Unit } from '@silbo/core';
import { ChevronSmallDownIcon, DoubleArrowUpAndDownIcon, FilterIcon } from '@silbo/ui';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SortOrderDialog from './SortOrderDialog';
import UnitFilterDialog from './UnitFilterDialog';

type CleaningMissionsToolbarProps = {
  sortOrder: CleaningMissionSortOrder;
  onSortOrderChange: (order: CleaningMissionSortOrder) => void;
  units: Unit[];
  selectedUnitIds: string[];
  onSelectedUnitIdsChange: (unitIds: string[]) => void;
};

type HeaderButtonProps = {
  icon: ReactNode;
  label: string;
  count?: number;
  onClick: () => void;
};

const HeaderButton = ({ icon, label, count, onClick }: HeaderButtonProps) => {
  const isHighlighted = count !== undefined;
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        minWidth: 0,
        height: 36,
        paddingX: 3,
        gap: '6px',
        borderRadius: '18px',
        bgcolor: isHighlighted ? 'common.white' : 'rgba(255, 255, 255, 0.14)',
        color: isHighlighted ? 'primary.main' : 'common.white',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: isHighlighted ? 600 : 500,
        '& .MuiSvgIcon-root': { fontSize: 16, flex: 'none' },
      }}
    >
      {icon}
      <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </Box>
      {isHighlighted && (
        <Box
          component="span"
          sx={{
            flex: 'none',
            minWidth: 18,
            height: 18,
            borderRadius: '9px',
            bgcolor: 'primary.main',
            color: 'common.white',
            fontSize: 11,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {count}
        </Box>
      )}
      <ChevronSmallDownIcon sx={{ fontSize: '12px !important' }} />
    </ButtonBase>
  );
};

/** Toujours sur une seule ligne : un libellé trop long est tronqué plutôt que de passer à la ligne. */
const CleaningMissionsToolbar = ({
  sortOrder,
  onSortOrderChange,
  units,
  selectedUnitIds,
  onSelectedUnitIdsChange,
}: CleaningMissionsToolbarProps) => {
  const { t } = useTranslation('environmental-cleaning');
  const [isSortOpen, setSortOpen] = useState(false);
  const [isFilterOpen, setFilterOpen] = useState(false);
  const hasUnitFilter = selectedUnitIds.length > 0;
  const unitFilterLabel = hasUnitFilter
    ? t('missions.unitFilter.selected', { count: selectedUnitIds.length })
    : t('missions.unitFilter.all');

  return (
    <Stack direction="row" alignItems="center" gap={2}>
      <HeaderButton
        icon={<DoubleArrowUpAndDownIcon />}
        label={t(`missions.sort.${sortOrder}`)}
        onClick={() => setSortOpen(true)}
      />
      <HeaderButton
        icon={<FilterIcon />}
        label={unitFilterLabel}
        count={hasUnitFilter ? selectedUnitIds.length : undefined}
        onClick={() => setFilterOpen(true)}
      />
      {isSortOpen && (
        <SortOrderDialog open sortOrder={sortOrder} onClose={() => setSortOpen(false)} onApply={onSortOrderChange} />
      )}
      {isFilterOpen && (
        <UnitFilterDialog
          open
          units={units}
          selectedUnitIds={selectedUnitIds}
          onClose={() => setFilterOpen(false)}
          onApply={onSelectedUnitIdsChange}
        />
      )}
    </Stack>
  );
};

export default CleaningMissionsToolbar;
